// Stampcard V2 ("Klippekort") — pure rules shared by the wallet and the cart.
// Both of these mirror server behaviour; keep them in step with the backend
// (src/controller/orders.js, src/services/voucher.js) rather than with each other.

/** A voucher is live in these statuses; the rest are history. */
export const READY_STATUSES = ['issued', 'reserved'];

/**
 * Is a voucher redeemable right now?
 *
 * Status alone is not enough: the server expires vouchers on a nightly sweep, so
 * one can read as `issued` with a past ExpiresAt — either fetched inside that
 * window, or sitting in a list we loaded before it lapsed. Offering a lapsed
 * voucher is worse than hiding it: the server rejects it silently and the order
 * goes through at full price after we quoted a discount.
 */
export const isVoucherReady = (voucher) => {
  const status = String(voucher?.Status || '').toLowerCase();
  if (!READY_STATUSES.includes(status)) return false;

  const expiresAt = voucher?.ExpiresAt;
  if (!expiresAt) return true; // null = never expires

  const ts = new Date(expiresAt).getTime();
  // An unparseable date must not hide a real voucher — let the server decide.
  return Number.isNaN(ts) || ts > Date.now();
};

/**
 * The order total once a reward voucher and a Regulars discount are applied.
 *
 * Order of operations is the whole point. The server subtracts the voucher from
 * the total FIRST, then computes loyalty on what is left:
 *
 *   total = max(0, total - voucherDiscount)          // orders.js
 *   computeLoyaltyDiscount(..., total - flatfee)
 *
 * Taking the percentage off the pre-voucher total instead overstates it, and the
 * customer is quoted less than they are charged.
 *
 * Display only — the server recomputes authoritatively at order creation.
 *
 * CALLER BEWARE (pages/RestaurantMenu): that component is ~3,600 lines, and
 * eslint-plugin-react-hooks 4.3.0 mis-analyses it if the JSX expression calling
 * this has no branch (`?:` or `||`) in it — every hook below ~line 2500 is then
 * reported as "called conditionally" and the build fails. It is a false positive
 * in the plugin's code-path analysis, not a real problem with the code. Keep the
 * `Number(voucherDiscount) || 0` at that call site.
 *
 * @param {object}  args
 * @param {number}  args.total          items subtotal, the loyalty base
 * @param {number}  args.grandTotal     subtotal + fees, before any discount
 * @param {number}  args.loyaltyPercent Regulars discount, as a percent
 * @param {number}  args.voucherDiscount reward value, absolute currency amount
 */
export const computeDiscountedTotal = ({
  total,
  grandTotal,
  loyaltyPercent,
  voucherDiscount,
} = {}) => {
  const voucher = Math.max(0, Number(voucherDiscount) || 0);
  const percent = Number(loyaltyPercent) || 0;

  const loyaltyBase = Math.max(0, (Number(total) || 0) - voucher);
  const loyaltyDiscountAmount = percent > 0 ? (loyaltyBase * percent) / 100 : 0;
  const adjustedGrandTotal = Math.max(
    0,
    (Number(grandTotal) || 0) - loyaltyDiscountAmount - voucher,
  );

  return { loyaltyDiscountAmount, adjustedGrandTotal };
};
